import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Segmented, Space, Table, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { createLatestGuard } from '@/utils/latestRequest';
import { istClock, istDayKey, istDayStart } from '@/utils/cricketCalendar';
import { LANGUAGE_LABEL, MOMENT_LABEL } from '@/utils/matchPushCopy';
import { liftPoints, pct, rateBase } from '@/utils/matchPushResults';
import type {
  MatchPushLanguageTest,
  MatchPushPlanResultRow,
  MatchPushResultsRow,
  PushLanguage,
} from '@/types/matchPushes';

const { Text } = Typography;
const DAY_MS = 24 * 60 * 60 * 1000;
const num = (n: number): string => n.toLocaleString('en-IN');

// The server caps results at 31 days (from today's start minus N days to tomorrow's start).
const WINDOWS = { 7: 'Last 7 days', 14: 'Last 14 days', 30: 'Last 30 days' } as const;
type WindowDays = keyof typeof WINDOWS;

/** Per-plan breakdown by language and version (loaded when a row opens). */
const PlanBreakdown: React.FC<{ planId: string }> = ({ planId }) => {
  const [rows, setRows] = useState<MatchPushPlanResultRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminApi.matchPushes
      .planResults(planId)
      .then((r) => {
        if (!cancelled) setRows(r.data.rows);
      })
      .catch((e) => {
        if (!cancelled) setError(apiErrorMessage(e, 'Failed to load the plan results'));
      });
    return () => {
      cancelled = true;
    };
  }, [planId]);

  if (error) return <Alert type="error" showIcon message={error} />;
  const holdoutRows = rows?.filter((r) => r.holdout) ?? [];
  const holdout = holdoutRows.reduce(
    (acc, r) => ({ base: acc.base + rateBase(true, r), back: acc.back + r.cameBack24h }),
    { base: 0, back: 0 }
  );

  return (
    <Table
      size="small"
      rowKey={(r) => `${r.language}-${r.version}-${r.holdout}`}
      loading={rows === null}
      pagination={false}
      dataSource={rows ?? []}
      columns={[
        {
          title: 'Language, version',
          key: 'arm',
          render: (_: unknown, r: MatchPushPlanResultRow) =>
            r.holdout ? (
              <Tag>No push (holdout)</Tag>
            ) : (
              `${LANGUAGE_LABEL[r.language as PushLanguage] ?? r.language}${r.version ? ` · ${r.version}` : ''}`
            ),
        },
        { title: 'Selected', dataIndex: 'selected', align: 'right' as const, render: num },
        { title: 'Delivered', dataIndex: 'delivered', align: 'right' as const, render: num },
        {
          title: 'Dropped (limit)',
          key: 'limited',
          align: 'right' as const,
          render: (_: unknown, r: MatchPushPlanResultRow) => num(r.limited ?? 0),
        },
        {
          title: 'Back in 2 h',
          key: 'b2',
          align: 'right' as const,
          render: (_: unknown, r: MatchPushPlanResultRow) =>
            pct(r.cameBack2h, rateBase(r.holdout, r)),
        },
        {
          title: 'Back in 24 h',
          key: 'b24',
          align: 'right' as const,
          render: (_: unknown, r: MatchPushPlanResultRow) =>
            pct(r.cameBack24h, rateBase(r.holdout, r)),
        },
        {
          title: 'Lift vs holdout',
          key: 'lift',
          align: 'right' as const,
          render: (_: unknown, r: MatchPushPlanResultRow) =>
            r.holdout
              ? '—'
              : liftPoints(r.cameBack24h, rateBase(false, r), holdout.back, holdout.base),
        },
      ]}
    />
  );
};

const ARM_LABEL = (arm: PushLanguage | 'holdout'): string =>
  arm === 'holdout' ? 'No push (holdout)' : LANGUAGE_LABEL[arm];

/** Results: each plan, and the language test (arm × came back 2 h and 24 h against the holdout). */
export const ResultsTab: React.FC = () => {
  const [days, setDays] = useState<WindowDays>(30);
  const [rows, setRows] = useState<MatchPushResultsRow[]>([]);
  const [test, setTest] = useState<MatchPushLanguageTest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<string | null>(null);

  const loadGuard = useRef(createLatestGuard());

  const load = useCallback(async () => {
    const isLatest = loadGuard.current.begin();
    setLoading(true);
    setError(null);
    try {
      const todayStart = istDayStart(istDayKey(Date.now())).getTime();
      const params = {
        from: new Date(todayStart - days * DAY_MS).toISOString(),
        to: new Date(todayStart + DAY_MS).toISOString(),
      };
      const [results, languageTest] = await Promise.all([
        adminApi.matchPushes.results(params),
        adminApi.matchPushes.languageTest(params),
      ]);
      if (!isLatest()) return;
      setRows(results.data.items);
      setTest(languageTest.data);
      setLoadedAt(new Date().toISOString());
    } catch (e) {
      if (isLatest()) setError(apiErrorMessage(e, 'Failed to load the results'));
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const holdoutArm = test?.rows.find((r) => r.arm === 'holdout');
  const holdoutBase = holdoutArm ? rateBase(true, holdoutArm) : 0;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space wrap>
        <Segmented
          options={Object.entries(WINDOWS).map(([value, label]) => ({
            value: Number(value),
            label,
          }))}
          value={days}
          onChange={(v) => setDays(v as WindowDays)}
        />
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {loadedAt ? `Loaded ${istClock(loadedAt)} IST. ` : ''}The server keeps results for up to
          10 minutes, so a refresh may show the same numbers.
        </Text>
      </Space>
      {error && <Alert type="error" showIcon message={error} />}
      <Card size="small" title="Per match push" loading={loading && rows.length === 0}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Came back = the player started a session within 24 h of the push (or of the time it would
          have gone, for the holdout). Lift is the difference in percentage points.
        </Text>
        <Table
          size="small"
          rowKey={(r) => r.plan.id}
          loading={loading}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          dataSource={rows}
          scroll={{ x: 'max-content' }}
          expandable={{ expandedRowRender: (r) => <PlanBreakdown planId={r.plan.id} /> }}
          columns={[
            {
              title: 'Match',
              key: 'match',
              render: (_: unknown, r: MatchPushResultsRow) => (
                <div>
                  <Text strong>
                    {r.plan.fixture.team1Name} vs {r.plan.fixture.team2Name}
                  </Text>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {MOMENT_LABEL[r.plan.moment]}
                      {r.plan.fixture.matchLabel ? ` · ${r.plan.fixture.matchLabel}` : ''}
                    </Text>
                  </div>
                </div>
              ),
            },
            { title: 'Selected', dataIndex: 'selected', align: 'right' as const, render: num },
            { title: 'Delivered', dataIndex: 'delivered', align: 'right' as const, render: num },
            {
              title: 'Dropped (limit)',
              key: 'limited',
              align: 'right' as const,
              render: (_: unknown, r: MatchPushResultsRow) => num(r.limited ?? 0),
            },
            { title: 'Holdout', dataIndex: 'holdout', align: 'right' as const, render: num },
            {
              title: 'Back in 24 h',
              key: 'back',
              align: 'right' as const,
              render: (_: unknown, r: MatchPushResultsRow) => pct(r.cameBack24h, r.delivered),
            },
            {
              title: 'Holdout back in 24 h',
              key: 'hback',
              align: 'right' as const,
              render: (_: unknown, r: MatchPushResultsRow) => pct(r.holdoutCameBack24h, r.holdout),
            },
            {
              title: 'Lift',
              key: 'lift',
              align: 'right' as const,
              render: (_: unknown, r: MatchPushResultsRow) =>
                liftPoints(r.cameBack24h, r.delivered, r.holdoutCameBack24h, r.holdout),
            },
          ]}
        />
      </Card>
      <Card size="small" title="Language test" loading={loading && !test}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Hindi-family players are split into English, Hinglish and Hindi, all match pushes
          together. The holdout got no push, so it shows what would have happened anyway. Pushes
          counts send rows: a player who got several pushes counts once for each.
        </Text>
        <Table
          size="small"
          rowKey="arm"
          loading={loading}
          pagination={false}
          dataSource={test?.rows ?? []}
          columns={[
            { title: 'Arm', dataIndex: 'arm', render: ARM_LABEL },
            { title: 'Pushes', dataIndex: 'players', align: 'right' as const, render: num },
            { title: 'Delivered', dataIndex: 'delivered', align: 'right' as const, render: num },
            {
              title: 'Back in 2 h',
              key: 'b2',
              align: 'right' as const,
              render: (_: unknown, r: MatchPushLanguageTest['rows'][number]) =>
                pct(r.cameBack2h, rateBase(r.arm === 'holdout', r)),
            },
            {
              title: 'Back in 24 h',
              key: 'b24',
              align: 'right' as const,
              render: (_: unknown, r: MatchPushLanguageTest['rows'][number]) =>
                pct(r.cameBack24h, rateBase(r.arm === 'holdout', r)),
            },
            {
              title: 'Lift vs holdout',
              key: 'lift',
              align: 'right' as const,
              render: (_: unknown, r: MatchPushLanguageTest['rows'][number]) =>
                r.arm === 'holdout'
                  ? '—'
                  : liftPoints(
                      r.cameBack24h,
                      rateBase(false, r),
                      holdoutArm?.cameBack24h ?? 0,
                      holdoutBase
                    ),
            },
          ]}
        />
      </Card>
    </Space>
  );
};
